/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as DataServiceEntities from './DataServiceEntities.g';
import { mWPPreferredSalesRepresentativesAsync } from './DataActionExtension.g';

/**
 * GetSalesRep Input Action
 */
export class GetSalesRepInput implements Msdyn365.IActionInput {
    public readonly preferredSalesRepEntity: DataServiceEntities.IMWPPreferredSalesRepEntity;

    constructor(preferredSalesRepEntity: DataServiceEntities.IMWPPreferredSalesRepEntity) {
        this.preferredSalesRepEntity = preferredSalesRepEntity;
    }

    public getCacheKey = (): string => `GetSalesRep`;
    public getCacheObjectType = (): string => 'SalesRepresentative';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

/**
 * Creates the GetSalesRepInput required to call the action
 */
const createInput = (
    args: Msdyn365.ICreateActionContext,
    preferredSalesRepEntity: DataServiceEntities.IMWPPreferredSalesRepEntity
): Msdyn365.IActionInput => {
    return new GetSalesRepInput(preferredSalesRepEntity);
};

/**
 * Action to fetch preferred sales representative details
 */
async function action(input: GetSalesRepInput, ctx: Msdyn365.IActionContext): Promise<DataServiceEntities.IMWPPreferredSalesRepResponse> {
    if (!input.preferredSalesRepEntity) {
        return Promise.reject('Preferred Sales Representative entity not provided');
    }

    try {
        // Fetch data using mWPPreferredSalesRepresentativesAsync
        const response = await mWPPreferredSalesRepresentativesAsync(
            { callerContext: ctx, bypassCache: 'getAndPut' },
            input.preferredSalesRepEntity
        );

        if (!response) {
            throw new Error('No data returned from the preferred sales representative API');
        }

        return response;
    } catch (error) {
        ctx.telemetry.error(`Error fetching preferred sales representative data: ${error}`);
        return Promise.reject(`Failed to fetch sales representative data: ${error.message}`);
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<DataServiceEntities.IMWPPreferredSalesRepResponse>>action,
    id: 'GetSalesRep',
    input: createInput,
    isBatched: false // Set to true if batched actions are required
});
